import React, { useState, useEffect, useRef } from 'react';
import { getUser, updateUserAvatar } from '../services/api';

// —– Add these at the top —–
const S3_BUCKET   = process.env.REACT_APP_S3_BUCKET;
const AWS_REGION  = process.env.REACT_APP_AWS_REGION;
const S3_BASE_URL =
  process.env.REACT_APP_S3_BASE ||
  `https://${S3_BUCKET}.s3.${AWS_REGION}.amazonaws.com`;
// —————————————————

/**
 * ProfilePic component
 * Props:
 * - userId: ID of the user whose avatar to display
 * - editable: boolean, if true clicking the avatar opens file picker & uploads
 * - size: number, width/height of the avatar in pixels (defaults to 80) test
 * - username: optional — when present, used to render a monogram (first
 *   letter, uppercase) as the no-avatar fallback in place of the literal
 *   "Profile" placeholder. Callers that have the username on hand should
 *   pass it; everywhere else still renders the generic glyph.
 */
const ProfilePic = ({ userId, editable = false, size = 80, username = null }) => {
  const [avatarKey, setAvatarKey] = useState(null);  // change name to key
  const [error, setError]       = useState('');
  const inputRef                = useRef();

  // Fetch avatar *key* from API on mount or when userId changes
  useEffect(() => {
    (async () => {
      try {
        const res  = await getUser(userId);
        const user = res.data?.user || res.data?.response || res.data;
        setAvatarKey(user.avatar_path || null);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    })();
  }, [userId]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');

    // Preview locally
    const preview = URL.createObjectURL(file);
    setAvatarKey(preview);  // temporarily treat preview as a full URL

    try {
      const uploadRes = await updateUserAvatar(userId, file);
      const updatedUser = uploadRes?.data?.user || uploadRes?.data?.response || uploadRes?.data;
      if (updatedUser?.avatar_path) {
        setAvatarKey(updatedUser.avatar_path);
      } else {
        // Re-fetch if the response didn't include avatar_path
        const res = await getUser(userId);
        const user = res.data?.user || res.data?.response || res.data;
        setAvatarKey(user.avatar_path);
      }
    } catch (err) {
      console.error('Upload error:', err);
      // Surface the most specific message available, in priority order:
      //   1. Our presign-flow throws (start with "File is too large" /
      //      "S3 upload failed" — both contain enough info to act on).
      //   2. The backend Connect-RPC error body (`response.data.message`)
      //      when the failure was UpdateAvatar / PresignUpload itself.
      //   3. A generic fallback for network errors with nothing useful.
      const presignMsg = typeof err?.message === 'string'
        && (err.message.startsWith('File is too large')
          || err.message.startsWith('S3 upload failed')
          || err.message.startsWith('S3 upload blocked'))
        ? err.message
        : null;
      const serverMsg = err?.response?.data?.message
        || err?.response?.data?.error
        || null;
      setError(presignMsg || serverMsg || 'Failed to upload avatar. Please try a smaller image or sign in again.');
    }
  };

  // Pick the smallest resized variant that still looks crisp at the
  // requested display size. Variants are uploaded by the backend's
  // resizeAndUpload helper at 100w/300w/full. Multiply by 2 for HiDPI/
  // retina displays before bucketing — a 60px avatar on a retina screen
  // wants ~120w of source, which lands in the medium bucket.
  const variantForSize = (px) => {
    const needed = px * 2;
    if (needed <= 100) return 'thumb';
    if (needed <= 300) return 'medium';
    return ''; // full original
  };

  // withImageSize inserts the variant suffix before the extension. Blob
  // preview URLs from URL.createObjectURL never match the regex so they
  // fall through unchanged.
  const withImageSize = (url, sz) => {
    if (!url || !sz) return url;
    return url.replace(/\.(jpe?g|png|gif|webp)$/i, `_${sz}.jpg`);
  };

  // Helper: if we only have the S3 key, build the full URL, then swap in
  // the resized variant matching the requested display size.
  const getSrc = (keyOrUrl) => {
    if (!keyOrUrl) return null;
    let full;
    // if it already looks like a full URL (preview or absolute), just use it
    if (keyOrUrl.startsWith('http') || keyOrUrl.startsWith('blob:')) full = keyOrUrl;
    else full = `${S3_BASE_URL}/${keyOrUrl}`;
    return withImageSize(full, variantForSize(size));
  };

  const sizePx = `${size}px`;
  const src    = getSrc(avatarKey);

  return (
    // The container is sized to the avatar exactly — width: sizePx —
    // so when an upload error renders below the image, the wrapper
    // doesn't expand horizontally and squeeze the surrounding grid
    // column (which used to compress the profile hero's title/bio
    // into a one-word-per-line sliver). Error text wraps inside the
    // narrow column instead.
    <div style={{ display: 'inline-block', width: sizePx, textAlign: 'center' }}>
      {editable && (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      )}
      <div
        onClick={() => editable && inputRef.current.click()}
        style={{
          width: sizePx,
          height: sizePx,
          borderRadius: '50%',
          overflow: 'hidden',
          // iOS Safari rendering bug: overflow:hidden alone doesn't
          // always clip rounded children. clip-path enforces the
          // circular crop via WebKit's own clipping pipeline.
          // isolation establishes a stacking context so the clip
          // applies cleanly to nested <img>. Without these two
          // lines, retina avatars on iOS spilled past the circle
          // outline (visible in user-reported screenshots).
          clipPath: 'circle(50%)',
          isolation: 'isolate',
          cursor: editable ? 'pointer' : 'default',
          background: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: src ? 'none' : '1px solid #ccc'
        }}
        title={editable ? 'Click to change avatar' : ''}
      >
        {src ? (
          <img
            src={src}
            alt="Profile"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          // Monogram fallback — first letter of the caller-supplied username,
          // uppercase. Replaces the literal "Profile" placeholder text that
          // used to render and looked sloppy in hero cards. When no username
          // is on hand we still show a neutral glyph rather than blanking the
          // tile.
          <span
            style={{
              fontSize: Math.max(12, size * 0.42),
              fontWeight: 700,
              color: '#94a3b8',
              lineHeight: 1,
              userSelect: 'none',
            }}
            aria-hidden="true"
          >
            {username && username.length > 0
              ? username.charAt(0).toUpperCase()
              : '·'}
          </span>
        )}
      </div>
      {error && (
        <p
          style={{
            color: 'red',
            marginTop: 6,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default ProfilePic;
